import React, { useEffect, useRef, useState } from 'react';
import cc from 'classcat';

import { PhotoModal } from '../../index';

import styles from './styles.module.scss';

interface Props {
  photos: AssetFragment[];
}

const LIGHTBOX_CLASS = 'menorah-lightbox-open';

const MenorahGallery = ({ photos }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(1);
  const observerRef = useRef<MutationObserver | null>(null);
  const thumbTrackRef = useRef<HTMLDivElement>(null);
  const thumbRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selected = photos[selectedIndex];

  // Scroll active thumbnail into view
  useEffect(() => {
    const thumb = thumbRefs.current[selectedIndex];
    const track = thumbTrackRef.current;
    if (!thumb || !track) return;
    const thumbLeft = thumb.offsetLeft;
    const thumbWidth = thumb.offsetWidth;
    const trackWidth = track.offsetWidth;
    const scrollTarget = thumbLeft - trackWidth / 2 + thumbWidth / 2;
    track.scrollTo({ left: scrollTarget, behavior: 'smooth' });
  }, [selectedIndex]);

  // Add/remove body class to scope lightbox styles and track slide position
  useEffect(() => {
    if (lightboxOpen) {
      document.body.classList.add(LIGHTBOX_CLASS);
      setCurrentSlide(1);

      const startObserver = () => {
        const track = document.querySelector('.menorah-lightbox-open .react-multi-carousel-track');
        if (!track) {
          setTimeout(startObserver, 100);
          return;
        }

        const getActiveIndex = () => {
          const items = track.querySelectorAll('li');
          let active = 0;
          items.forEach(function(item, i) {
            if (item.getAttribute('aria-hidden') === 'false') {
              active = i;
            }
          });
          return (active % photos.length) + 1;
        };

        observerRef.current = new MutationObserver(() => {
          setCurrentSlide(getActiveIndex());
        });

        observerRef.current.observe(track, { attributes: true, attributeFilter: ['aria-hidden'], subtree: true });
      };

      setTimeout(startObserver, 200);
    } else {
      document.body.classList.remove(LIGHTBOX_CLASS);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    }

    return () => {
      document.body.classList.remove(LIGHTBOX_CLASS);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lightboxOpen, photos.length]);

  if (!photos.length) return null;

  const prevIndex = (selectedIndex - 1 + photos.length) % photos.length;
  const nextIndex = (selectedIndex + 1) % photos.length;

  const reorderedPhotos = [
    ...photos.slice(selectedIndex),
    ...photos.slice(0, selectedIndex),
  ];

  return (
    <>
      {/* Main hero image */}
      <div
        className={styles.mainView}
        role="button"
        tabIndex={0}
        aria-label="View full screen"
        onClick={() => setLightboxOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setLightboxOpen(true); }}
      >
        <img
          src={selected.url!}
          alt={selected.title || `Gallery image ${selectedIndex + 1}`}
          className={styles.mainImage}
        />
        <div className={styles.expandHint}>
          <span className={styles.expandIcon}>&#x26F6;</span>
          <span className={styles.expandLabel}>View full screen</span>
        </div>

        {/* Prev / next arrows */}
        {photos.length > 1 && (
          <>
            <button
              type="button"
              className={cc([styles.navArrow, styles.navArrowPrev])}
              aria-label="Previous image"
              onClick={(e) => { e.stopPropagation(); setSelectedIndex(prevIndex); }}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              type="button"
              className={cc([styles.navArrow, styles.navArrowNext])}
              aria-label="Next image"
              onClick={(e) => { e.stopPropagation(); setSelectedIndex(nextIndex); }}
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Thumbnail track */}
      {photos.length > 1 && (
        <div ref={thumbTrackRef} className={styles.thumbTrack}>
          {photos.map((photo, i) => (
            <button
              key={photo.url}
              ref={(el) => { thumbRefs.current[i] = el; }}
              type="button"
              className={cc({ [styles.thumb]: true, [styles.thumbActive]: i === selectedIndex })}
              onClick={() => setSelectedIndex(i)}
              aria-label={photo.title || `Gallery image ${i + 1}`}
            >
              <img
                src={photo.url!}
                alt={photo.title || `Thumbnail ${i + 1}`}
                loading="lazy"
                className={styles.thumbImage}
              />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <>
          <div className={styles.lightboxFooter}>
            <div className={styles.lightboxCounter}>
              {currentSlide} of {photos.length}
            </div>
            <div className={styles.progressBarTrack}>
              <div
                className={styles.progressBarFill}
                style={{ width: `${(currentSlide / photos.length) * 100}%` }}
              />
            </div>
          </div>
          <PhotoModal
            photos={reorderedPhotos}
            handleClose={() => setLightboxOpen(false)}
          />
        </>
      )}
    </>
  );
};

export default MenorahGallery;
